<div data-testid="settings-page" class="settings-page">
  <header class="settings-page__intro">
    <div class="settings-page__heading">
      <h2 data-testid="settings-title" class="settings-page__title">Prediction thresholds and health-score weights</h2>
      <p data-testid="settings-applies" class="settings-page__subtitle">The rules the nightly job flags companies with. Changes apply from the next nightly run (02:00 UTC), or press Recompute now.</p>
    </div>
    <div *ngIf="saved" class="settings-page__side">
      <p data-testid="settings-last-change" class="settings-lastchange">
        <i class="fa-regular fa-clock-rotate-left settings-lastchange__icon" aria-hidden="true"></i>
        <span>
          <ng-container *ngIf="saved.lastChange; else neverChanged">
            Last change by <b>{{ saved.updatedByName || saved.updatedBy }}</b> · {{ saved.updatedAt | date:'d MMM y, HH:mm':'UTC' }} UTC
            <br><span class="settings-lastchange__what">{{ saved.lastChange }}</span>
          </ng-container>
          <ng-template #neverChanged>Using the default values — not changed yet.</ng-template>
        </span>
      </p>
      <div *ngIf="canEdit" class="settings-page__actions">
        <button mat-button type="button" data-testid="settings-reset" [disabled]="saving || atDefaults" (click)="resetToDefaults()">Reset to defaults</button>
        <button mat-stroked-button class="adb-natural-width" type="button" data-testid="settings-discard" [disabled]="saving || !hasChanges" (click)="discard()">Discard</button>
        <button mat-flat-button color="primary" type="button" data-testid="settings-save" [disabled]="!canSave" (click)="save()">Save changes</button>
        <button mat-stroked-button class="adb-natural-width" type="button" data-testid="settings-recompute" [disabled]="recomputing || saving || hasChanges"
                [attr.title]="hasChanges ? 'Save or discard your changes first.' : null" (click)="recompute()">{{ recomputing ? 'Recomputing…' : 'Recompute now' }}</button>
      </div>
    </div>
  </header>
  <div *ngIf="unsavedChanges.length > 0" data-testid="settings-preview" class="settings-preview" role="status">
    <i class="fa-regular fa-circle-info settings-preview__icon" aria-hidden="true"></i>
    <span>{{ previewText }}</span>
  </div>
  <div *ngIf="error" data-testid="settings-error" class="settings-page__error">{{ error }}</div>
  <div *ngIf="loading" data-testid="settings-loading" class="settings-page__muted">Loading…</div>
  <div *ngIf="loadError" data-testid="settings-load-error" class="settings-page__error">The settings could not be loaded.</div>
  <div *ngIf="saved && !canEdit" data-testid="settings-readonly" class="settings-page__notice">Only administrators can change thresholds and weights.</div>
  <div *ngIf="saved" data-testid="settings-grid" class="settings-page__grid">
  <form [formGroup]="form" data-testid="settings-form" class="settings-page__form settings-page__main">
    <section *ngFor="let group of groups" formGroupName="thresholds" class="settings-card" [attr.data-testid]="'settings-group-' + group.id">
      <header class="settings-card__head">
        <h3 class="settings-card__title">{{ group.title }}</h3>
        <p class="settings-card__rule">{{ group.rule }}</p>
      </header>
      <div class="settings-rules">
        <div *ngFor="let field of group.fields" class="settings-rule" [attr.data-testid]="'settings-field-' + field.key"
             [attr.data-changed]="thresholdChanged(field) || null" [attr.data-invalid]="thresholdInvalid(field) || null">
          <span class="settings-rule__label">
            {{ field.label }}
            <span *ngIf="thresholdChanged(field)" class="settings-rule__changed"
                  [attr.data-testid]="'settings-changed-' + field.key">{{ thresholdChangedFrom(field) }}</span>
          </span>
          <div class="settings-rule__sentence">
            <span>{{ field.before }}</span>
            <span class="settings-rule__control">
              <span *ngIf="field.unit === 'usd'" class="settings-rule__affix">$</span>
              <mat-form-field appearance="outline" subscriptSizing="dynamic" class="adb-compact-field">
                <input matInput type="number" class="settings-rule__number" [formControlName]="field.key" [attr.min]="field.min"
                       [attr.max]="field.max" [attr.step]="field.step" [attr.aria-label]="field.label"
                       [attr.data-testid]="'settings-input-' + field.key">
              </mat-form-field>
              <span *ngIf="field.unit === 'pct'" class="settings-rule__affix">%</span>
              <span *ngIf="field.unit === 'times'" class="settings-rule__affix">×</span>
            </span>
            <span *ngIf="field.after">{{ field.after }}</span>
          </div>
          <p class="settings-rule__meta" [class.settings-rule__meta--error]="thresholdInvalid(field)"
             [attr.data-testid]="'settings-meta-' + field.key">
            <i *ngIf="thresholdInvalid(field)" class="fa-regular fa-circle-exclamation" aria-hidden="true"></i>
            {{ fieldMeta(field) }}
          </p>
          <p *ngIf="field.addition" class="settings-rule__addition">
            <i class="fa-regular fa-flask" aria-hidden="true"></i>
            <span>{{ field.addition }}</span>
          </p>
        </div>
      </div>
    </section>
    <section formGroupName="weights" class="settings-card" data-testid="settings-weights">
      <header class="settings-card__head">
        <h3 class="settings-card__title">Health-score weights</h3>
        <p class="settings-card__rule">How much each input counts towards the 0–100 company health score. They must add up to 100%.</p>
      </header>
      <div class="settings-rules">
        <div *ngFor="let field of weightFields" class="settings-rule settings-rule--weight"[attr.data-testid]="'settings-field-' + field.key"
             [attr.data-changed]="weightChanged(field) || null" [attr.data-invalid]="weightInvalid(field) || null">
          <span class="settings-rule__label">
            {{ field.label }}
            <span *ngIf="weightChanged(field)" class="settings-rule__changed"
                  [attr.data-testid]="'settings-changed-' + field.key">{{ weightChangedFrom(field) }}</span>
          </span>
          <span class="settings-rule__control">
            <mat-form-field appearance="outline" subscriptSizing="dynamic" class="adb-compact-field">
              <input matInput type="number" class="settings-rule__number" [formControlName]="field.key" [attr.min]="0"
                     [attr.max]="100" [attr.step]="1" [attr.aria-label]="field.label"
                     [attr.data-testid]="'settings-input-' + field.key">
            </mat-form-field>
            <span class="settings-rule__affix">%</span>
          </span>
          <span class="settings-rule__bar" aria-hidden="true">
            <span class="settings-rule__bar-fill" [attr.data-testid]="'settings-bar-' + field.key" [style.width.%]="weightBar(field)"></span>
          </span>
          <p class="settings-rule__meta" [class.settings-rule__meta--error]="weightInvalid(field)"
             [attr.data-testid]="'settings-meta-' + field.key">
            <i *ngIf="weightInvalid(field)" class="fa-regular fa-circle-exclamation" aria-hidden="true"></i>
            {{ weightMeta(field) }}
          </p>
        </div>
      </div>
      <div data-testid="settings-weights-total-box" class="settings-total" [attr.data-ok]="weightsTotal === 100 || null"
           role="status" aria-live="polite">
        <span class="settings-total__label">Total</span>
        <span data-testid="settings-weights-total" class="settings-total__value">{{ weightsTotal }}%</span>
        <span data-testid="settings-weights-message" class="settings-total__msg">
          <i *ngIf="weightsTotal === 100" class="fa-regular fa-circle-check" aria-hidden="true"></i>
          <i *ngIf="weightsTotal !== 100" class="fa-regular fa-circle-exclamation" aria-hidden="true"></i>
          {{ weightsMessage }}
        </span>
      </div>
      <div data-testid="settings-tierref" class="settings-tierref">
        <span class="settings-tierref__item" data-tier="critical">Critical below 40</span>
        <span class="settings-tierref__item" data-tier="warning">Warning 40–65</span>
        <span class="settings-tierref__item" data-tier="watch">Watch 65–80</span>
      </div>
    </section>
  </form>
  <aside data-testid="settings-aside" class="settings-page__aside" aria-label="Impact of these values">
    <app-admin-dashboard-settings-impact [impact]="impact" [state]="impactState" [loading]="impactLoading"
                                         [error]="impactError"></app-admin-dashboard-settings-impact>
  </aside>
  </div>
</div>
